import { useState, useEffect } from "react";
import { toast } from "sonner";
import API from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Compass, Trash, Info, ShieldCheck } from "@phosphor-icons/react";

/**
 * Admin dialog to set / update / clear an operative's tracker embed URL.
 * Accepts either a bare https URL or a full `<iframe …>` snippet. The
 * backend extracts just the src and rejects non-https URLs.
 *
 * Pass `operative` (object with user_id, name, tracker_embed_url) to open.
 * Pass `null` / `undefined` to keep closed.
 */
export default function SetTrackerDialog({ operative, onClose, onSaved }) {
  const [raw, setRaw] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (operative) {
      setRaw(operative.tracker_embed_url || "");
    }
  }, [operative]);

  if (!operative) return null;

  // Cheap client-side preview so the admin can sanity-check
  const previewUrl = (() => {
    const s = (raw || "").trim();
    if (!s) return "";
    const m = s.match(/<iframe[^>]*\bsrc\s*=\s*['"]([^'"]+)['"]/i);
    const candidate = m ? m[1] : s;
    return candidate.toLowerCase().startsWith("https://") ? candidate : "";
  })();

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put(`/in-house/team/${operative.user_id}/tracker`, { embed: raw });
      toast.success(data.tracker_embed_url ? "Tracker saved" : "Tracker cleared");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save tracker");
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setClearing(true);
    try {
      await API.put(`/in-house/team/${operative.user_id}/tracker`, { embed: "" });
      toast.success("Tracker cleared");
      onSaved && onSaved({ tracker_embed_url: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear tracker");
    } finally { setClearing(false); }
  };

  return (
    <Dialog open={!!operative} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="set-tracker-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo] flex items-center gap-2">
            <Compass className="h-5 w-5 text-blue-600" weight="duotone" />
            Tracker for {operative.name}
          </DialogTitle>
          <DialogDescription>
            Paste the iframe snippet your tracker provider gave you (Quartix, Movolytics, Geotab, RAM, etc.) — or just the bare URL.
            We&apos;ll extract the <code className="text-[11px] bg-muted px-1 py-0.5 rounded">src</code> automatically and store only that.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="tracker-embed-textarea" className="text-xs">Iframe snippet or https URL</Label>
            <Textarea
              id="tracker-embed-textarea"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder='<iframe src="https://tracker.example.com/vehicle/123" width="800" height="500"></iframe>'
              rows={5}
              className="font-mono text-xs"
              data-testid="set-tracker-input"
            />
          </div>

          {previewUrl && (
            <div className="text-xs flex items-start gap-2 p-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/60" data-testid="set-tracker-preview">
              <ShieldCheck size={14} weight="duotone" className="text-emerald-600 shrink-0 mt-0.5" />
              <span className="text-emerald-900 dark:text-emerald-200 break-all">
                We&apos;ll store: <span className="font-mono text-[11px]">{previewUrl}</span>
              </span>
            </div>
          )}

          {raw && !previewUrl && (
            <div className="text-xs flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60">
              <Info size={14} weight="duotone" className="text-amber-700 shrink-0 mt-0.5" />
              <span className="text-amber-900 dark:text-amber-200">
                That doesn&apos;t look like a valid https URL. Make sure it starts with <code className="text-[11px] bg-amber-100 dark:bg-amber-900/40 px-1 rounded">https://</code>.
              </span>
            </div>
          )}

          {operative.tracker_embed_url && (
            <p className="text-[11px] text-muted-foreground">
              Currently saved: <span className="font-mono break-all">{operative.tracker_embed_url}</span>
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          {operative.tracker_embed_url && (
            <Button
              variant="outline"
              onClick={clear}
              disabled={clearing || saving}
              className="border-destructive/40 text-destructive hover:bg-destructive/10 mr-auto"
              data-testid="set-tracker-clear-btn"
            >
              <Trash size={12} className="mr-1" /> {clearing ? "Clearing…" : "Clear tracker"}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving || clearing} data-testid="set-tracker-cancel-btn">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || clearing} data-testid="set-tracker-save-btn">
            {saving ? "Saving…" : "Save tracker"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
